import { useMutation } from 'hooks/apollo';
import { useCallback, useState } from 'react';
import saveAs from 'file-saver';
import { EXPORT_ENTITY, IMPORT_ENTITY } from './mutations';
import { getContentFromFileEvent, getExportFileData, parseAndValidateType } from './functions';
import { useToast } from 'hooks/toasts/useToast';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { useTranslation } from 'react-i18next';

/**
 * Export entity functionality encapsulated in a hook
 * @param {number} id Id of the entity to export
 * @param {string} type Type of the entity, corresponding to the .NET object type
 */
export function useEntityExport(id, type) {
  const { t } = useTranslation();
  const addToast = useToast();

  const [exporting, setExporting] = useState(false);

  const downloadExport = useCallback(
    transferObject => {
      const [content, extension, blobType] = getExportFileData(transferObject);
      if (!content) return;
      const blob = new Blob([content], { type: blobType });
      saveAs(blob, `${type}_${id}.${extension}`);
    },
    [id, type]
  );

  const [exportEntityMutation] = useMutation(EXPORT_ENTITY, {
    onCompleted: data => {
      setExporting(false);
      const transferObject = parseAndValidateType(data.exportEntity, type);
      if (!transferObject) {
        addToast(t('Transfer.ExportInvalid'), 'error');
        return;
      }
      downloadExport(transferObject);
      addToast(t('Transfer.ExportSuccess'), 'success');
    },
    onError: error => {
      setExporting(false);
      addToast(error, 'error');
    }
  });

  const exportEntity = useCallback(() => {
    setExporting(true);
    exportEntityMutation({ variables: { id, type } });
  }, [id, type, exportEntityMutation]);

  return { exporting, exportEntity };
}

/**
 * Import entity functionality encapsulated in a hook
 * @param {string} type Type of the entity, corresponding to the .NET object type
 * @param {string} redirectUrl Optional redirect url for the imported entity; must contain placeholder for id "{ID}"
 */
export function useEntityImport(type, redirectUrl) {
  const historyContext = useHistoryWithContext();
  const { t } = useTranslation();
  const addToast = useToast();

  const [importing, setImporting] = useState(false);

  const [importEntityMutation] = useMutation(IMPORT_ENTITY, {
    onCompleted: data => {
      setImporting(false);
      const id = data.importEntity;
      if (!id) {
        console.error('Could not determine if import was successful. Id missing!');
        return;
      }
      addToast(t('Transfer.ImportSuccess'), 'success');
      if (!window.confirm(t('Transfer.NavigateToEntity'))) return;
      if (redirectUrl && redirectUrl.includes('{ID}')) historyContext.push(redirectUrl.replace('{ID}', id));
      else if (redirectUrl) console.error(`Given redirectUrl does not contain id placeholder ${redirectUrl}`);
    },
    onError: () => {
      setImporting(false);
    }
  });

  const importEntity = useCallback(
    serializedEntity => {
      if (!parseAndValidateType(serializedEntity, type)) {
        addToast(t('Transfer.ImportInvalid'), 'error');
        return;
      }
      setImporting(true);
      importEntityMutation({ variables: { serializedEntity } });
    },
    [type, addToast, t, importEntityMutation]
  );

  const importEntityFromFile = useCallback(
    event => {
      getContentFromFileEvent(event, content => importEntity(content));
    },
    [importEntity]
  );

  return { importing, importEntity, importEntityFromFile };
}
